// アクションの自動実行（推奨アクション）関連のドメインモデル
import { PriorityLevel } from '@/common/model/media/articleCreator'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'

// ステータス（タブ）
export const MediaActionStatus = {
  SUGGESTED: 'suggested',
  IN_PROGRESS: 'in_progress',
  AUTO_EXECUTED: 'auto_executed',
  DONE: 'done',
  REJECTED: 'rejected'
} as const
export type MediaActionStatus = (typeof MediaActionStatus)[keyof typeof MediaActionStatus]

// アクション種別
export const MediaActionType = {
  CREATE_ARTICLE: 'create_article',
  REWRITE_ARTICLE: 'rewrite_article',
  ADD_INTERNAL_LINK: 'add_internal_link',
  RESOLVE_CANNIBALIZATION: 'resolve_cannibalization',
  STRATEGIC_ACTION: 'strategic_action',
  INVESTIGATE: 'investigate'
} as const
export type MediaActionType = (typeof MediaActionType)[keyof typeof MediaActionType]

// 自動実行のステップログ
export interface ExecLogStep {
  step?: string
  name?: string
  label?: string
  status?: string
  ok?: boolean
  started_at?: string
  completed_at?: string
  timestamp?: string
  history_id?: number
  char_count?: number
  title?: string
  primary_keyword?: string
  error?: string
}

// 自動記事作成の実行メタ（auto_draft_meta）。実行ログ表示に使う
// AI分解で生成された子アクションの要約
export interface DecomposedActionSummary {
  id: number
  action_type: string
  title: string
  priority: PriorityLevel
  target_keywords?: string[]
  confidence?: PriorityLevel
  classification_source?: string
  reasoning?: string
  target_page_id?: string
}

export interface AutoDraftMeta {
  phase?: string
  ok?: boolean
  executed_at?: string
  completed_at?: string
  steps?: ExecLogStep[]
  error?: string
  primary_keyword?: string
  draft_status?: string
  next_step?: string
  keyword_analysis_history_id?: number
  // 戦略アクションのAI分解の結果（分解結果モーダルの表示に使う）
  decompose_summary?: string
  decomposed?: DecomposedActionSummary[]
  // 自動記事作成（auto_article_creation）で実際に作成された記事の参照先
  created_article_proposal_id?: number
  created_article_id?: number
  created_article_title?: string
  created_article_version?: number
  [key: string]: unknown
}

// アクションの分類メタ（prefilled_params 内）
export interface ActionPrefilledParams {
  classification_confidence?: PriorityLevel
  classification_source?: string
  classification_reasoning?: string
  target_page_id?: string
  // リライト対象が自社の記事案として存在する場合の記事案ID（編集画面への深リンク用）
  article_proposal_id?: number
  current_rank?: number
  investigation_items?: string[]
  [key: string]: unknown
}

export interface MediaAction {
  id: number
  action_type: MediaActionType | string
  source_analysis_type: MediaAnalysisType | null
  source_history_id: number | null
  title: string
  description: string
  priority: PriorityLevel
  prefilled_params: ActionPrefilledParams | null
  status: MediaActionStatus | string
  rejection_note: string | null
  suggested_at: string | null
  started_at: string | null
  completed_at: string | null
  rejected_at: string | null
  scheduled_date: string | null
  start_planned_date: string | null
  execution_order: number
  target_keywords: string[]
  auto_draft_meta: AutoDraftMeta | null
  source_link: string | null
  // 自動記事作成フラグ（true の間は他の操作不可。5分毎のバックグラウンドジョブが処理する）
  auto_article_creation_requested: boolean
  // AI分解の親（分解元の戦略アクション）。分解で生成された子アクションのみ値を持つ
  parent_action_id: number | null
}

// 詳細（なぜ推奨されたか）
export interface ActionDetail {
  action: MediaAction
  source_summary: {
    history_id: number
    analysis_type: string
    summary: string | null
    created_at: string | null
    input: Record<string, unknown>
  } | null
  source_raw: Record<string, unknown> | null
  source_link: string | null
  // AI分解の親子関係。children は分解で生成された子、parent は分解元の戦略アクション
  children: MediaAction[]
  parent: MediaActionParent | null
}

// AI分解の親アクション（分解元の戦略アクション）の要約
export interface MediaActionParent {
  id: number
  action_type: string
  title: string
  status: string
}

// 実行ログ（exec-log-bulk の1件）
export interface ActionExecLog {
  action_id: number
  title: string
  action_type: string
  status: string
  started_at: string | null
  completed_at: string | null
  phase?: string
  ok?: boolean
  steps?: ExecLogStep[]
  error?: string | null
}

// 自動スケジュール案の1件
export interface SchedulePlanItem {
  action_id: number
  scheduled_date: string | null
  execution_order: number
  reason?: string
  title: string
  priority: PriorityLevel
  action_type: string
  source_analysis_type?: string
  target_keywords?: string[]
}
